import { GridCallbackDetails, GridFilterModel, GridPaginationModel, GridSortModel, ValueOptions } from '@mui/x-data-grid-premium';
import React, { useEffect } from 'react';
import { useConfirmation } from 'src/base/ConfirmationDialog/ConfirmationService';
import { DataGrid, IGridColDef, Stack, IconButton, Button, Dialog, Grid } from 'src/components';
import ParametreGrupEnum from 'src/enums/ParametreGrupEnum';
import { translate } from 'src/i18n/tHelper';
import { ApiRequest } from 'src/models/api.request';
import VwHastaUyarilisteDTO from 'src/models/HastaUyariModels/VwHastaUyarilisteDTO';
import RecordInfo from 'src/parts/Genel/record.info';
import { ApiService } from 'src/services/core';
import { useAppDispatch, useAppSelector } from 'src/store';
import DialogTitle from 'src/components/DialogTitle';
import DialogContent from 'src/components/DialogContent';
import DialogActions from 'src/components/DialogActions';
import { HastaKaraListeSlice } from 'src/store/Slices/HastaUyariListesi/HastaKaraListeSlice';
import { useHastaKaraListeSilMutation } from 'src/services/HastaUyariListesi/HastaKaraListeApi';
import { helper } from 'src/utils/helper';

const translatePath = "pages|hastaUyariListesi";
const HastaKaraListesi = (): JSX.Element => {
    const state = useAppSelector(s => s.hastaKaraListe);
    const kimlikState = useAppSelector(s => s.kimlik);
    const parameters = useAppSelector(s => s.parameters.data);
    const dispatch = useAppDispatch();
    const confirm = useConfirmation();

    const [sil, silState] = useHastaKaraListeSilMutation();

    const query = async (req: ApiRequest<VwHastaUyarilisteDTO>) => {
        var service = new ApiService<VwHastaUyarilisteDTO>("HastaKaraListe");
        var res = await service.post("HastaKaraListesi", req);
        if (res.responseCode !== 200) {
            confirm({
                description: res.errorMessage,
                variant: "info",
            });
            dispatch(HastaKaraListeSlice.actions.setData({
                ...res,
                isFetching: false,
                isLoading: false,
            }));
        }
        dispatch(HastaKaraListeSlice.actions.setData({
            ...res,
            isFetching: false,
            isLoading: false,
        }));
    };

    useEffect(() => {
        if (!state.data.isLoading) return;
        dispatch(HastaKaraListeSlice.actions.setData({
            ...state.data,
            isFetching: true,
        }));
        let req: ApiRequest<VwHastaUyarilisteDTO> = {
            paging: true,
            pageIndex: state.paginationModel.page,
            pageSize: state.paginationModel.pageSize,
            filters: { groupOperator: "and", predicates: [] },
            sorts: []
        };
        if (state.filterModel && state.filterModel.items?.length > 0) {
            state.filterModel.items.forEach(filterItem => {
                const operator = filterItem.operator === "contains" ? "like" :
                    filterItem.operator === "equals" ? "=" :
                        filterItem.operator === "startsWith" ? "startsWith" :
                            filterItem.operator === "endsWith" ? "endsWith" :
                                filterItem.operator === "isEmpty" ? "empty" :
                                    filterItem.operator === "isNotEmpty" ? "empty" :
                                        "like";

                req.filters.predicates.push({
                    fieldName: filterItem.field as keyof VwHastaUyarilisteDTO,
                    operator: operator as "endsWith" | "startsWith" | "=" | ">" | ">=" | "<" | "<=" | "like" | "empty" | "in" | "between",
                    value1: filterItem.value,
                    value2: null,
                    not: filterItem.operator === "isNotEmpty" ? true : false,
                });
            });
        }
        if (state.sortModel && state.sortModel?.length > 0) {
            state.sortModel.forEach(sortItem => {
                req.sorts.push({
                    fieldName: sortItem.field as keyof VwHastaUyarilisteDTO,
                    ascending: sortItem.sort === 'asc'
                });
            });
        } else {
            req.sorts.push({ fieldName: "id", ascending: false });
        }
        query(req);
    }, [state.paginationModel, state.sortModel, state.filterModel, state.data.isLoading]);

    const handleEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwHastaUyarilisteDTO): void => {
        dispatch(HastaKaraListeSlice.actions.setActiveRow(row));
        dispatch(HastaKaraListeSlice.actions.setEkleOpen(true));
    }
    const handleDelete = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwHastaUyarilisteDTO) => {
        if (!silState.isLoading) {
            const confirmResult = window.confirm(translate(translatePath, "Kayıt silinecektir, emin misiniz?"));
            if (confirmResult) {
                await sil(row).unwrap().then(() => {
                    dispatch(HastaKaraListeSlice.actions.setData({
                        ...state.data,
                        isLoading: true,
                    }));
                }).catch(e => e);
            }
        }
    }

    const columns: IGridColDef<VwHastaUyarilisteDTO>[] = [
        {
            field: "id",
            headerName: translate(translatePath, "Kayıt No"),
            flex: 1,
            minWidth: 100,
            type: "number",
            noThousandSeparator: true,
        },
        {
            field: "kimlikId",
            headerName: translate(translatePath, "Dosya No"),
            type: "number",
            noThousandSeparator: true,
            flex: 1,
            minWidth: 100,
        },
        {
            field: "tckimlikno",
            headerName: translate(translatePath, "T.C. Kimlik No"),
            align: "right",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "ad",
            headerName: translate(translatePath, "Adı"),
            flex: 1,
            minWidth: 100,
        },
        {
            field: "soyad",
            headerName: translate(translatePath, "Soyadı"),
            flex: 1,
            minWidth: 100,
        },
        {
            field: "babaad",
            headerName: translate(translatePath, "Baba Adı"),
            flex: 1,
            minWidth: 100,
        },
        {
            field: "fkGrup",
            headerName: translate(translatePath, "Grubu"),
            flex: 1,
            minWidth: 150,
            type: "singleSelect",
            valueOptions: (params) => {
                return parameters.filter((f) => f.grupId === ParametreGrupEnum.HastaUyariGrubu).map((m) => {
                    return { value: m.key, label: m.value } as ValueOptions;
                });
            },
        },
        {
            field: "aktif",
            headerName: translate(translatePath, "Aktif"),
            width: 70,
            type: "boolean",
            valueGetter: (params) => { return params.row?.aktif === "E"; },
        },
        {
            field: "kullanici",
            headerName: translate(translatePath, "Ekleyen Kullanıcı"),
            flex: 1,
            minWidth: 100,
        },
        {
            field: "eklemeTarihi",
            headerName: translate(translatePath, "Ekleme Tarihi"),
            flex: 1,
            minWidth: 155,
            type: "dateTime",
        },
        {
            field: "action",
            headerName: translate(translatePath, "İşlemler"),
            width: 120,
            renderCell: (params) => {
                return (
                    <Stack direction="row">
                        <IconButton customType="edit" onClick={(e) => { handleEdit(e, params.row); }} />
                        <IconButton customType="delete" loading={params.row?.id === silState.originalArgs?.id && silState.isLoading} onClick={(e) => { handleDelete(e, params.row); }} />
                        <RecordInfo
                            id={params.row?.id}
                            eklemeTarihi={params.row.eklemeTarihi}
                            ekleyenKullanici={params.row.ekleyenKullanici}
                            guncellemeTarihi={params.row.guncellemeTarihi}
                            guncelleyenKullanici={params.row.guncelleyenKullanici}
                        />
                    </Stack>
                );
            },
        },
    ];

    const onPaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails): void => {
        dispatch(HastaKaraListeSlice.actions.setPaginationModel(model));
    }
    const onSortModelChange = (model: GridSortModel, details: GridCallbackDetails): void => {
        dispatch(HastaKaraListeSlice.actions.setSortModel(model));
    }
    const onFilterModelChange = (model: GridFilterModel, details: GridCallbackDetails<"filter">): void => {
        dispatch(HastaKaraListeSlice.actions.setFilterModel(model));
    }

    const handleSearch = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (!state.data.isFetching)
            dispatch(HastaKaraListeSlice.actions.setData({
                ...state.data,
                isLoading: true,
            }));
        else
            dispatch(HastaKaraListeSlice.actions.setData({
                ...state.data,
                isLoading: false,
            }));
    }
    const handlerAdd = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        let kontrol: boolean = false;
        let row: VwHastaUyarilisteDTO = new VwHastaUyarilisteDTO();
        row.id = 0;
        row.fkIslem = 1;
        if (kimlikState.activeRow?.id > 0) {
            kontrol = true;
        }
        if (kontrol) {
            const confirmResult = window.confirm(translate(translatePath, "Arka plandaki kimlik ile hızlı ekleme yapmak ister misiniz?"));
            if (confirmResult) {
                row.kimlikId = kimlikState.activeRow.id;
                row.tckimlikno = helper.objectToNumber(kimlikState.activeRow.kimlikNo);
                row.pasaportno = kimlikState.activeRow.pasaportNo;
                row.ad = kimlikState.activeRow.adi;
                row.soyad = kimlikState.activeRow.soyadi;
                row.babaad = kimlikState.activeRow.babaAdi;
                dispatch(HastaKaraListeSlice.actions.setActiveRow(row));
                dispatch(HastaKaraListeSlice.actions.setEkleOpen(true));
            } else {
                dispatch(HastaKaraListeSlice.actions.setActiveRow({ ...new VwHastaUyarilisteDTO(), id: 0, fkIslem: 1, }));
                dispatch(HastaKaraListeSlice.actions.setEkleOpen(true));
            }
        } else {
            dispatch(HastaKaraListeSlice.actions.setActiveRow({ ...new VwHastaUyarilisteDTO(), id: 0, fkIslem: 1, }));
            dispatch(HastaKaraListeSlice.actions.setEkleOpen(true));
        }
    }
    const handleClose = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        dispatch(HastaKaraListeSlice.actions.setOpen(false));
    };
    return (
        <Dialog fullWidth open={state.open} maxWidth="lg">
            <DialogTitle>{translate(translatePath, "Hasta Uyarı Listesi Ekranı")}</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid item>
                        <DataGrid
                            unstable_headerFilters
                            searchButton={<Button customType="search" onClick={handleSearch}>{state.data.isFetching ? translate(translatePath, "Aramayı Durdur") : translate(translatePath, "Ara")}</Button>}
                            addButton={<Button customType="add" onClick={handlerAdd} />}
                            pinnedColumns={{ right: ["action"] }}
                            sx={{ height: "60vh" }}
                            loading={state.data.isFetching}
                            rows={state.data?.data || []}
                            rowCount={state.data?.totalRecords || 0}
                            columns={columns}
                            pagination={true}
                            paginationMode="server"
                            sortingMode="server"
                            filterMode="server"
                            paginationModel={state.paginationModel}
                            onPaginationModelChange={onPaginationModelChange}
                            sortModel={state.sortModel}
                            onSortModelChange={onSortModelChange}
                            filterModel={state.filterModel}
                            onFilterModelChange={onFilterModelChange}
                            slots={{
                                headerFilterMenu: null
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container justifyContent="flex-end">
                    <Grid item md={4} lg={3}>
                        <Button customType="close" onClick={handleClose} />
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

export default HastaKaraListesi;